import React from 'react';
import { AlertInline, Box, Link, themeVars } from '@eds-web/react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useHistory } from 'react-router';

import { usePrizeStepNavigation } from '../../hooks/usePrizeStepNavigation';
import { StepComponentProps } from './PrizeStep';
import { PrizeSteps } from './PrizeSteps';
import { Step1 } from './Step1';
import { Step2 } from './Step2';
import { Step3 } from './Step3';
import { Step4 } from './Step4';
import { Winning } from '../../types/prize.types';
import { StyledCurrentPrize, StyledPrizeProgressBar } from './Prizes.style';
import { PRIZE_STEP } from '../../constants/prize';
import { usePrizeSteps } from '../../hooks/usePrizeSteps';
import { useInterviewFlow } from '../../hooks/useInterviewFlow';
import { device } from '../../constants/breakpoint';
import { selectIsValidatedTaxProfile } from '../../selectors/tax.selector';
import { getEnhancedStepProps } from '../../utils/prizeUtils';
import { PRIZE_V2_ROUTER } from '../../routes/routes';
import { useLocale } from '../../hooks/useLocale';
import { selectAccountIsUnder18 } from '../../selectors/account.selector';

interface StepComponentMap {
    [key: number]: React.ComponentType<StepComponentProps>;
}

export enum STEP_STATUS {
    COMPLETE = 'COMPLETE',
    ACTIVE = 'ACTIVE',
    INCOMPLETE = 'INCOMPLETE'
}

const stepComponents: StepComponentMap = {
    [PRIZE_STEP.STEP1]: Step1,
    [PRIZE_STEP.STEP2]: Step2,
    [PRIZE_STEP.STEP3]: Step3,
    [PRIZE_STEP.STEP4]: Step4
};

const getCurrentStepComponent = (
    currentStep: number,
    props?: StepComponentProps
): React.ReactElement | null => {
    const StepComponent = stepComponents[currentStep];
    return StepComponent ? <StepComponent {...props} /> : null;
};

interface PrizeContentDetailProps {
    currentStep: number;
    winning?: Winning;
    identityTaxVerified: boolean;
    isOffline?: boolean;
    isCash?: boolean;
    onAgeGateVisibilityChange?: (showAgeGate: boolean) => void;
}

const StyledPrizeContentDetail = styled.div`
    @media screen and ${device.laptop} {
        gap: 16px;
        div {
            box-sizing: border-box;
            max-width: 100%;
        }
    }
`;

const StyledPrizeStepContent = styled(Box)`
    display: flex;
    @media screen and ${device.laptop} {
        display: block;
    }
`;

export const PrizeContentDetail: React.FC<PrizeContentDetailProps> = ({
    currentStep,
    winning,
    identityTaxVerified,
    isOffline,
    isCash
}) => {
    const isTaxInterviewCompleted = useSelector(selectIsValidatedTaxProfile);
    const history = useHistory();
    const { dir } = useLocale();
    const isUnder18 = useSelector(selectAccountIsUnder18);
    const { step, goToStep, goToNextStep, goToPreviousStep } = usePrizeStepNavigation({
        initialStep: currentStep,
        maxStep: currentStep
    });

    const steps = usePrizeSteps(
        isCash,
        identityTaxVerified,
        isOffline,
        winning,
        isTaxInterviewCompleted
    );

    const getStepStatus = (stepIndex: number): STEP_STATUS => {
        if (stepIndex < step) {
            return STEP_STATUS.COMPLETE; // Steps before current step are complete
        } else if (stepIndex === step) {
            return STEP_STATUS.ACTIVE; // Current step is active
        } else {
            return STEP_STATUS.INCOMPLETE; // Steps after current step are incomplete
        }
    };

    const isStepCompleted = (stepIndex: number): boolean => {
        return stepIndex < currentStep; // Steps before max step are completed
    };

    const { handleButtonClick } = useInterviewFlow({
        prizeId: winning?.prizeId,
        eventName: winning?.eventName,
        isRetake: false
    });

    const handleLinkClickNavigateToTab = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const pathname = `${PRIZE_V2_ROUTER}?tab=tax`;
        history.push(pathname);
    };

    const enhancedStepProps = getEnhancedStepProps({
        step,
        currentStep,
        winning,
        isCash,
        isOffline,
        identityTaxVerified,
        goToNextStep,
        goToPreviousStep,
        getStepStatus,
        isStepCompleted,
        additionalProps: {
            onButtonClick: handleButtonClick
        }
    });

    return (
        <StyledPrizeContentDetail>
            {!isCash && (
                <AlertInline
                    style={{ marginBottom: 16 }}
                    status={'informational'}
                    message={
                        isTaxInterviewCompleted ? (
                            <Trans
                                i18nKey="epic.esport.progress.title.pg.v2"
                                components={{
                                    a: (
                                        <Link
                                            style={{
                                                color: themeVars.color.transparent.light['100']
                                            }}
                                            onClick={handleLinkClickNavigateToTab}
                                        />
                                    )
                                }}
                            />
                        ) : (
                            <Trans
                                i18nKey="epic.esport.progress.title.pg.invalid.v2"
                                components={{
                                    a: (
                                        <Link
                                            style={{
                                                color: themeVars.color.transparent.light['100']
                                            }}
                                            onClick={() => handleButtonClick(!isUnder18)}
                                        />
                                    )
                                }}
                            />
                        )
                    }
                />
            )}
            <StyledPrizeStepContent>
                <StyledPrizeProgressBar $dir={dir}>
                    <PrizeSteps
                        currentStep={step - 1}
                        onStepChange={goToStep}
                        maxStep={currentStep}
                        steps={steps}
                    />
                </StyledPrizeProgressBar>
                <StyledCurrentPrize>
                    {getCurrentStepComponent(step, enhancedStepProps)}
                </StyledCurrentPrize>
            </StyledPrizeStepContent>
        </StyledPrizeContentDetail>
    );
};
